'use client';

import { clientFetch } from '@/lib/api/clientFetch';
import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { BsCheck2Circle } from 'react-icons/bs';
import './newsletter.scss';

export default function AuthNewsletter({ currentUser = null, messages = {} }) {
  const [newsEmail, setNewsEmail] = useState(currentUser?.email || '');
  const [isSubscribed, setIsSubscribed] = useState(
    Boolean(currentUser?.isSubscribed),
  );
  const [isPending, setIsPending] = useState(false);
  const [feedback, setFeedback] = useState({ tone: '', message: '' });

  const title = messages.title || 'Newsletter';
  const text =
    messages.text || 'Subscribe to get the latest updates and offers.';
  const formTitle = messages.formTitle || 'newsletter form';
  const placeholder =
    messages?.form?.newsEmail?.placeholder || 'Enter email...';
  const subscribeLabel = messages?.button?.submit || 'Subscribe';
  const subscribedText =
    messages.subscribedText || 'You are already subscribed to our newsletter.';
  const unsubscribeLink = messages?.unsubscribe?.link || 'Click Here';
  const unsubscribeText = messages?.unsubscribe?.text || 'to unsubscribe.';

  if (!currentUser?.userNumber || !currentUser?.email) {
    return null;
  }

  const submitHandler = async (event) => {
    event.preventDefault();

    const normalizedEmail = newsEmail.trim().toLowerCase();

    setIsPending(true);
    setFeedback({ tone: '', message: '' });

    try {
      await clientFetch('/api/users/newsletter', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({ newsEmail: normalizedEmail }),
      });

      setNewsEmail(normalizedEmail);
      setIsSubscribed(true);
      setFeedback({
        tone: 'success',
        message: 'Subscription saved. You will receive future updates here.',
      });
    } catch (error) {
      setFeedback({
        tone: 'error',
        message:
          error?.data?.message ||
          'Could not subscribe right now. Please try again.',
      });
    } finally {
      setIsPending(false);
    }
  };

  const unsubscribeHandler = async () => {
    setIsPending(true);
    setFeedback({ tone: '', message: '' });

    try {
      await clientFetch(`/api/users/${currentUser.userNumber}/newsletter`, {
        method: 'DELETE',
      });

      setIsSubscribed(false);
      setFeedback({
        tone: 'success',
        message: 'You have been unsubscribed from newsletter updates.',
      });
    } catch (error) {
      setFeedback({
        tone: 'error',
        message:
          error?.data?.message ||
          'Could not unsubscribe right now. Please try again.',
      });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className="newsletter-panel newsletter-panel--auth">
      {isSubscribed ? (
        <>
          <h6 className="text-white mb-2">
            <BsCheck2Circle size={36} className="text-warning" />{' '}
            {subscribedText}
          </h6>
          <p className="fs-6 d-flex align-items-center gap-1 mb-2">
            <Button
              variant="link"
              onClick={unsubscribeHandler}
              disabled={isPending}
              className="custom-btn-link footer-link text-warning"
            >
              {unsubscribeLink}
            </Button>{' '}
            <span>{unsubscribeText}</span>
          </p>
        </>
      ) : (
        <>
          <h5 className="footer-heading mb-1 mb-sm-3">{title}</h5>
          <p className="mb-2">{text}</p>
          <Form title={formTitle} onSubmit={submitHandler}>
            <Form.Group>
              <Form.Control
                type="email"
                id="newsLetterEmail"
                name="newsLetterEmail"
                placeholder={placeholder}
                spellCheck="false"
                autoComplete="email"
                value={newsEmail}
                onChange={(event) => setNewsEmail(event.target.value)}
                className="mb-2 text-center rounded-0"
                readOnly
                disabled={isPending}
              />
              <Button
                type="submit"
                variant="info"
                disabled={isPending}
                className="newsletter-submit"
              >
                {isPending ? 'Saving...' : subscribeLabel}
              </Button>
            </Form.Group>
          </Form>
        </>
      )}

      {feedback.message ? (
        <p
          className={`newsletter-feedback mt-2 mb-0 is-${feedback.tone}`.trim()}
          role="status"
          aria-live="polite"
        >
          {feedback.message}
        </p>
      ) : null}
    </div>
  );
}
